import { useState, useRef, useCallback } from 'react';
import { SectionWrapper } from '../SectionWrapper';
import { CreditCard } from 'lucide-react';
import { POSIntegrationSection } from '../sections/POSIntegrationSection';
import { POSReportsStep2Combined } from '../phase2/POSReportsStep2Combined';
import { POSReportsStep3Training } from '../phase2/POSReportsStep3Training';
import { POSReportsStep3Review } from '../phase2/POSReportsStep3Review';
import { POSReportsStep4 } from '../phase2/POSReportsStep4';
import { useOnboarding } from '@/contexts/OnboardingContext';

const TOTAL_STEPS = 5; // Step 1: POS Integration Setup, Steps 2-5: Training (Upload, Map, Review, Complete)

interface POSTrainingCombinedProps {
  onComplete?: () => void | Promise<void>;
  onSkip?: () => void | Promise<void>;
  onSaveAndReturn?: () => void | Promise<void>;
}

export const POSTrainingCombined = ({ onComplete, onSkip, onSaveAndReturn }: POSTrainingCombinedProps) => {
  const { completeSection } = useOnboarding();
  const isAdvancingRef = useRef(false); // Prevent multiple advances

  // Always start at step 1 (Platform Setup) when entering POS training.
  // Advance to step 2 only when the user clicks Continue on step 1.
  const [currentStep, setCurrentStep] = useState(1);

  const handleNext = useCallback(async () => {
    if (isAdvancingRef.current) return; // Prevent double-clicks
    
    if (currentStep === 1) {
      // After POS integration setup, mark pos_integration as completed and move to training
      try {
        isAdvancingRef.current = true;
        await completeSection('pos_integration');
        setCurrentStep(2);
      } catch (error) {
        console.error('Error completing POS integration:', error);
      } finally {
        isAdvancingRef.current = false;
      }
    } else if (currentStep < TOTAL_STEPS) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Complete the training
      if (onComplete) {
        await onComplete();
      }
    }
  }, [currentStep, completeSection, onComplete]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleSkip = async () => {
    if (onSkip) {
      await onSkip();
    }
  };

  const getStepName = () => {
    switch (currentStep) {
      case 1: return 'Platform Setup';
      case 2: return 'Upload Reports';
      case 3: return 'Map Menu Items';
      case 4: return 'Review Mappings';
      case 5: return 'Complete';
      default: return '';
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <POSIntegrationSection />;
      case 2:
        return (
          <POSReportsStep2Combined
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 3:
        return (
          <POSReportsStep3Training
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 4:
        return (
          <POSReportsStep3Review
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 5:
        return (
          <POSReportsStep4
            onNext={handleNext}
            onPrevious={handlePrevious}
            onComplete={async () => {
              // Save module completion to onboarding_progress and navigate (page's onComplete)
              if (onComplete) await onComplete();
            }}
          />
        );
      default:
        return null;
    }
  };

  // Step 1 renders POSIntegrationSection directly (no step wrapper).
  // Pass onCompleteAndAdvance so when user clicks Complete Setup we advance to step 2.
  if (currentStep === 1) {
    return (
      <POSIntegrationSection
        onCompleteAndAdvance={() => setCurrentStep(2)}
      />
    );
  }

  // Steps 2-5 use our wrapper
  return (
    <SectionWrapper
      title="POS Training"
      description="Train AI on your POS system reports."
      icon={<CreditCard className="h-6 w-6" />}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onSkip={handleSkip}
      onSaveAndReturn={onSaveAndReturn}
      canSkip
      nextLabel={currentStep === TOTAL_STEPS ? 'Complete' : 'Continue'}
    >
      <div className="space-y-6">
        {/* Progress indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              Step {currentStep} of {TOTAL_STEPS}: {getStepName()}
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round((currentStep / TOTAL_STEPS) * 100)}% complete
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all"
              style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>

        {/* Step content */}
        {renderStep()}
      </div>
    </SectionWrapper>
  );
};

